// [ai] #ds-layer, mounted once inside .at-shell: the audio pair the engine crossfades between, the drop, the video window, the folders
// card, the sheet, the menu, toasts, help. It also owns the document-level wiring: boot (server settings → hash or Continue pill), the
// router's hash (a #rec= the page navigates to opens paused), rec:/note:/play links anywhere on the page, the KEYMAP, pagehide.
import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router";
import { parseLink, type Link } from "../core/links";
import { usePlayer } from "../stores/player";
import { useShellStore } from "../stores/shell";
import { Browser } from "./Browser";
import { usePlayerCtl } from "./context";
import { Drop } from "./Drop";
import { Help } from "./Help";
import { Menu } from "./Menu";
import type { Player } from "./player";
import { Sheet } from "./Sheet";
import { Toasts } from "./Toasts";
import { usePlayerKeys } from "./useKeys";
import { Video } from "./Video";
const booted = new WeakSet<Player>();   // StrictMode mounts twice; the player boots once
export function PlayerLayer() {
  const p = usePlayerCtl(); const shell = useShellStore(); const loc = useLocation(); const panelOpen = usePlayer(s => s.panel.open);
  const a = useRef<HTMLAudioElement>(null); const b = useRef<HTMLAudioElement>(null);
  usePlayerKeys(p, shell);
  useLayoutEffect(() => { p.mountAudio(a.current!, b.current!); }, [p]);
  useEffect(() => { if (booted.has(p)) return; booted.add(p); void p.init({ pathname: loc.pathname, hash: loc.hash }); }, [p]);   // eslint-disable-line react-hooks/exhaustive-deps -- boot reads the first location only
  // [ai] Router hash changes after boot (Back/Forward, a Link with a hash): the player decides — its own silent writes and a stale
  // progress hash of the open recording are ignored, a real deep link opens paused.
  const first = useRef(true);
  useEffect(() => { if (first.current) { first.current = false; return; } p.hashChanged(loc.hash); }, [loc.hash, p]);
  useEffect(() => {
    const click = (e: MouseEvent) => {
      const a = (e.target as Element).closest<HTMLElement>("a[href], [data-rec], [data-note]"); if (!a || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button) return;
      const link: Link | null = a.dataset.rec ? { kind: "rec", id: a.dataset.rec, t: +(a.dataset.t || 0), e: a.dataset.e ? +a.dataset.e : null } : a.dataset.note ? { kind: "note", id: a.dataset.note, at: +(a.dataset.at || 0), len: +(a.dataset.len || 0) } : parseLink(a.getAttribute("href"));
      if (!link) return; e.preventDefault(); p.openLink(link);   // no stopPropagation: React listens on the root, and the Sheet closes itself on a cite click
    };
    const hide = () => p.pagehide();
    document.addEventListener("click", click, true); window.addEventListener("pagehide", hide);
    return () => { document.removeEventListener("click", click, true); window.removeEventListener("pagehide", hide); };
  }, [p]);
  return (
    <div id="ds-layer">
      <audio ref={a} className="ds-audio" preload="metadata" /><audio ref={b} className="ds-audio" preload="metadata" />
      {panelOpen ? <div className="ds-scrim" onClick={() => p.togglePanel(false)} /> : null}
      <Drop /><Video /><Browser /><Sheet /><Menu /><Toasts /><Help />
    </div>
  );
}
